import type { FilterItemValue } from '../types/SearchVehicleFilters';
import type { IVehicle } from '@makemydeal/dr-platform-types';
import { CDL_V3_EVENT } from '@makemydeal/dr-shared-types';
import type { AiAltVehicleOption } from '../types/AiAltVehicleOption';
import { changeVehicleConfig } from '../store/config';

// Only these two fields of a filter reach a CDL payload — mapFiltersToBooleanFlags reads `id` to find the tag's field
// and `value` to decide whether it is set. `id` is a plain string rather than the filter-group enums because the
// lookup below is expected to miss: an id it does not recognise is skipped, and that case is worth being able to pass
// in. A real AppliedFilter stays assignable, so no caller changes.
type CDLFilterFields = { id: string; value: FilterItemValue };

// Types for CDL search data
type SearchValue = string | string[] | number | boolean;

type SearchData = Record<string, SearchValue>;

// CDL Event payload structure
interface CDLEventConfig {
    tagIndex: string;
    element: string;
    action: string;
    intention: string;
    location: string | string[];
    eventText?: string;
}

// Extended vehicle data that may include additional properties beyond IVehicle
interface CDLVehicleData extends Partial<IVehicle> {
    cadsVehicleCatalogId?: string;
    acceptanceLikelihood?: string;
}

interface CDLEventPayload extends CDLEventConfig {
    search?: SearchData;
    vehicle?: CDLVehicleData;
    buildOptions?: Record<string, unknown>;
    application?: { name: string | undefined };
}

interface CDLEvent {
    type: typeof CDL_V3_EVENT;
    payload: CDLEventPayload;
}

// Maps filter group IDs to CDL spec field names
const FILTER_ID_TO_CDL_FIELD: Record<string, string> = {
    inventoryTypes: 'inventoryType',
    years: 'year',
    makes: 'make',
    models: 'model',
    trims: 'trim',
    bodyStyles: 'bodyStyle',
    price: 'price',
    mileage: 'mileage',
    engines: 'fuelType',
    extColors: 'exteriorColor',
    intColors: 'interiorColor',
    dealerships: 'dealerShip',
    age: 'ageOfInventory'
};

// Maps applied filters to boolean flags (true/false) per CDL spec
const mapFiltersToBooleanFlags = (appliedFilters: CDLFilterFields[]): Record<string, boolean> => {
    const flags: Record<string, boolean> = {};

    // Initialize all known fields to false
    Object.values(FILTER_ID_TO_CDL_FIELD).forEach((field) => {
        flags[field] = false;
    });

    if (!appliedFilters || appliedFilters.length === 0) return flags;

    appliedFilters.forEach((filter: CDLFilterFields) => {
        const filterId = filter.id;
        const cdlField = FILTER_ID_TO_CDL_FIELD[filterId];
        if (cdlField && Boolean(filter.value)) {
            flags[cdlField] = true;
        }
    });

    return flags;
};

const getLocation = (): string => changeVehicleConfig?.applicationName || 'Inventory Search';

const getApplication = () => ({ name: changeVehicleConfig?.applicationName });

const mapVehicleToCDLData = (vehicle: CDLVehicleData): CDLVehicleData => ({
    vin: vehicle.vin,
    make: vehicle.make,
    model: vehicle.model,
    year: vehicle.year,
    cadsVehicleCatalogId: vehicle.cadsVehicleCatalogId
});

// Generic CDL event creator
const createCDLEvent = (eventConfig: CDLEventConfig, additionalData: Partial<CDLEventPayload> = {}): CDLEvent => {
    const eventData: CDLEventPayload = {
        ...eventConfig,
        ...additionalData
    };

    return {
        type: CDL_V3_EVENT,
        payload: eventData
    };
};

// Page Load Events

/**
 * vs-001: Opens the Change Vehicle section
 */
export const createPageLoadEvent = () => {
    return createCDLEvent(
        {
            element: 'Page',
            action: 'View',
            intention: 'Access vehicle search functionality',
            location: 'Inventory Search',
            tagIndex: 'vs-001'
        },
        {
            application: getApplication()
        }
    );
};

// Search Events

/**
 * vs-002: Filter application (existing - enhanced)
 */
export const createVehicleSearchFilterEvent = ({
    appliedFilters,
    resultsCount,
    searchText,
    aiMode
}: {
    appliedFilters: CDLFilterFields[];
    resultsCount: number;
    searchText?: string;
    aiMode: boolean;
}) => {
    const searchFilters = mapFiltersToBooleanFlags(appliedFilters);
    const element = aiMode ? 'AI-Powered Vehicle Search' : 'Vehicle Search';
    const intention = aiMode ? 'Search for vehicle using AI' : 'Search for vehicle with VIN or stock number';

    return createCDLEvent(
        {
            element,
            action: 'Update',
            intention,
            location: getLocation(),
            tagIndex: 'vs-002'
        },
        {
            search: {
                ...searchFilters,
                resultsRank: resultsCount,
                ...(searchText && { keywords: searchText })
            },
            application: getApplication()
        }
    );
};

/**
 * vs-009: Free text search utilization
 */
export const createTextSearchEvent = (searchText: string, resultsCount: number) => {
    return createCDLEvent(
        {
            element: 'Search Vehicle',
            action: 'Click',
            intention: 'Switch to vehicle search based on VIN or stock number',
            location: 'Inventory Search',
            tagIndex: 'vs-009'
        },
        {
            search: {
                searchText,
                resultsRank: resultsCount
            }
        }
    );
};

/**
 * Search results received successfully
 */
export const createSearchResultsEvent = (resultsCount: number, searchType: 'filter' | 'text' | 'build') => {
    return createCDLEvent(
        {
            element: 'Search Results',
            action: 'Load',
            intention: 'Display search results',
            location: 'Inventory Search',
            tagIndex: 'vs-results'
        },
        {
            search: {
                resultsCount,
                searchType
            }
        }
    );
};

/**
 * Sorting feature utilization
 */
export const createSortingEvent = (sortBy: string, resultsCount: number) => {
    return createCDLEvent(
        {
            element: 'Sort Options',
            action: 'Change',
            intention: 'Sort search results',
            location: 'Inventory Search',
            tagIndex: 'vs-sort'
        },
        {
            search: {
                sortBy,
                resultsRank: resultsCount
            }
        }
    );
};

/**
 * Pagination feature utilization
 */
export const createPaginationEvent = (page: number, pageSize: number, totalResults: number) => {
    return createCDLEvent(
        {
            element: 'Pagination',
            action: 'Navigate',
            intention: 'View more search results',
            location: 'Inventory Search',
            tagIndex: 'vs-pagination'
        },
        {
            search: {
                currentPage: page,
                pageSize,
                totalResults
            }
        }
    );
};

// Vehicle Interaction Events

/**
 * vs-003: Click on specific vehicle card (selection without applying)
 * Location is dynamically set based on the host application (e.g., 'Manager View', 'Sales Check-In')
 */
export const createVehicleSelectionEvent = ({
    vehicle,
    appliedFilters,
    resultsRank,
    searchQuery,
    aiMode
}: {
    vehicle: IVehicle;
    appliedFilters: CDLFilterFields[];
    resultsRank: number;
    searchQuery?: string;
    aiMode: boolean;
}) => {
    const searchFilters = mapFiltersToBooleanFlags(appliedFilters);
    const score = vehicle._score;
    const element = aiMode ? 'AI-Powered Vehicle Search Result Box' : 'Vehicle Search Result Box';

    return createCDLEvent(
        {
            element,
            action: 'Click',
            intention: 'Select vehicle',
            location: getLocation(),
            tagIndex: 'vs-003'
        },
        {
            vehicle: mapVehicleToCDLData(vehicle),
            search: {
                ...searchFilters,
                resultsRank,
                ...(searchQuery && { searchTerm: searchQuery }),
                ...(typeof score === 'number' && { consumerAcceptanceScore: score })
            },
            application: getApplication()
        }
    );
};

/**
 * vs-005: Cancel Vehicle Information Update
 */
export const createCancelVehicleUpdateEvent = (vehicle: IVehicle, appliedFilters: CDLFilterFields[], resultsRank: number) => {
    const searchFilters = mapFiltersToBooleanFlags(appliedFilters);

    return createCDLEvent(
        {
            element: 'Cancel Vehicle Information Update Button',
            action: 'Click',
            intention: 'Cancel vehicle information update for the selected vehicle',
            location: getLocation(),
            tagIndex: 'vs-005'
        },
        {
            vehicle: mapVehicleToCDLData(vehicle),
            search: {
                ...searchFilters,
                resultsRank
            }
        }
    );
};

// Build Vehicle Events

/**
 * vs-006: Build Vehicle Search button utilization
 */
export const createBuildVehicleSearchEvent = (buildOptions: Record<string, unknown>) => {
    return createCDLEvent(
        {
            element: 'Build Vehicle Search Button',
            action: 'Click',
            intention: 'View vehicles based on custom vehicle feature selection',
            location: 'Inventory Search',
            tagIndex: 'vs-006'
        },
        {
            buildOptions
        }
    );
};

/**
 * vs-019: Build Vehicle Result Box click (select vehicle from build results)
 */
export const createBuildVehicleSelectedEvent = (vehicle: IVehicle, resultsRank: number, searchTerm: string) => {
    const score = vehicle._score;

    return createCDLEvent(
        {
            element: 'Build Vehicle Result Box',
            action: 'Click',
            intention: 'Select vehicle',
            location: getLocation(),
            tagIndex: 'vs-019'
        },
        {
            vehicle: mapVehicleToCDLData(vehicle),
            search: {
                resultsRank,
                searchTerm,
                ...(typeof score === 'number' && { consumerAcceptanceScore: score })
            },
            application: getApplication()
        }
    );
};

/**
 * vs-020: Update Vehicle from build results
 */
export const createBuildVehicleUpdateEvent = (vehicle: IVehicle, resultsRank: number, searchTerm: string) => {
    const score = vehicle._score;

    return createCDLEvent(
        {
            element: 'Update Vehicle Button',
            action: 'Click',
            intention: 'Update selected vehicle from build vehicle result list',
            location: getLocation(),
            tagIndex: 'vs-020'
        },
        {
            vehicle: mapVehicleToCDLData(vehicle),
            search: {
                resultsRank,
                searchTerm,
                ...(typeof score === 'number' && { consumerAcceptanceScore: score })
            },
            application: getApplication()
        }
    );
};

/**
 * vs-023: New Scenario button click for an AVO-selected vehicle (create new deal scenario).
 */
export const createNewScenarioEvent = (vehicle?: IVehicle) => {
    return createCDLEvent(
        {
            element: 'Create New Scenario from AVO Result List',
            action: 'Click',
            intention: 'Create new deal scenario from AVO result list',
            location: ['Vehicle Search'],
            tagIndex: 'vs-023'
        },
        {
            ...(vehicle?.vin && { vehicle: mapVehicleToCDLData(vehicle) }),
            application: getApplication()
        }
    );
};

/**
 * vs-027: the "Select Vehicle" action (inline row button or the footer's own apply) for a non-AVO, non-build vehicle
 * on the Change Vehicle screen.
 */
export const createSelectVehicleFromResultListEvent = (vehicle?: IVehicle) => {
    return createCDLEvent(
        {
            element: 'Select Vehicle Button',
            action: 'Click',
            intention: 'Pick vehicle from search result list',
            location: ['Vehicle Search'],
            tagIndex: 'vs-027'
        },
        {
            ...(vehicle?.vin && { vehicle: mapVehicleToCDLData(vehicle) }),
            application: getApplication()
        }
    );
};

/**
 * vs-028: New Scenario button click for a non-AVO vehicle (create new deal scenario).
 */
export const createNewScenarioFromResultListEvent = (vehicle?: IVehicle) => {
    return createCDLEvent(
        {
            element: 'Create New Scenario from Result List',
            action: 'Click',
            intention: 'Create new deal scenario',
            location: ['Vehicle Search'],
            tagIndex: 'vs-028'
        },
        {
            ...(vehicle?.vin && { vehicle: mapVehicleToCDLData(vehicle) }),
            application: getApplication()
        }
    );
};

/**
 * dc-mv-252: AI Vehicle Search Button click
 */
export const createAiSearchEvent = (searchQuery: string) => {
    return createCDLEvent({
        element: 'Search Vehicles AI Powered Search',
        action: 'Click',
        intention: 'Search for vehicles with AI support',
        location: 'Deal Dashboard',
        tagIndex: 'dc-mv-252',
        eventText: searchQuery
    });
};

// Vehicle Switch - Selective Deal Component (Item Retention Modal) Events

/**
 * dc-mv-318: "Proceed" (Deal Details Confirmation) button click in the item retention
 * (selective deal component) modal — preserves the selected deal details when switching vehicles.
 */
export const createDealDetailsConfirmationEvent = () => {
    return createCDLEvent({
        element: 'Deal Details Confirmation Button',
        action: 'Click',
        intention: 'Preserves selected deal details, including rebates, incentives, and VPP, when switching vehicles.',
        location: 'Deal Dashboard',
        tagIndex: 'dc-mv-318'
    });
};

/**
 * dc-mv-319: "Keep all" / "Remove all" deal details toggle click in the item retention modal.
 * `keepAll` reflects the toggle's resulting state and drives the dynamic element label.
 */
export const createDealDetailsRetentionToggleEvent = (keepAll: boolean) => {
    return createCDLEvent({
        element: `${keepAll ? 'Keep all' : 'Remove all'} Deal Details Toggle`,
        action: 'Click',
        intention: 'Removing or confirming deal details when switching a vehicle',
        location: 'Deal Dashboard',
        tagIndex: 'dc-mv-319'
    });
};

/**
 * dc-mv-320: "Proceed & Open Programs" button click in the item retention modal.
 */
export const createProceedAndOpenProgramsEvent = () => {
    return createCDLEvent({
        element: 'Proceed & Open Programs Button',
        action: 'Click',
        intention: 'Proceed with current deal details selection and open programs',
        location: 'Deal Dashboard',
        tagIndex: 'dc-mv-320'
    });
};

/**
 * vs-026: an Alternative Vehicle Options card was displayed on the Change Vehicle screen (replaces dc-mv-261 here).
 */
export const createAiVehicleOptionsDisplayedEvent = (recommendation: AiAltVehicleOption) => {
    const { vin, make, model, year, chromeId } = recommendation.vehicle;
    return createCDLEvent(
        {
            element: 'AI Supported Vehicle Suggestions Card',
            action: 'View',
            intention: 'Show AI based alternative vehicle selection',
            location: ['Vehicle Search'],
            tagIndex: 'vs-026'
        },
        {
            vehicle: {
                vin,
                make,
                model,
                year,
                cadsVehicleCatalogId: chromeId,
                acceptanceLikelihood: recommendation.affordability.level.toLowerCase()
            },
            application: getApplication()
        }
    );
};

/**
 * vs-024: the "Select Vehicle" button on an Alternative Vehicle Options card was clicked on the Change Vehicle screen.
 */
export const createSelectVehicleEvent = (vehicle?: IVehicle) => {
    return createCDLEvent(
        {
            element: 'Pick Vehicle from AVO Result List',
            action: 'Click',
            intention: 'Pick the selected vehicle from search result list (AVO)',
            location: ['Vehicle Search'],
            tagIndex: 'vs-024'
        },
        {
            ...(vehicle?.vin && { vehicle: mapVehicleToCDLData(vehicle) }),
            application: getApplication()
        }
    );
};

/**
 * vs-025: the Alternative Vehicle Options accordion was expanded on the Change Vehicle screen.
 */
export const createAiVehicleOptionsAccordionEvent = (vehicle?: IVehicle) => {
    return createCDLEvent(
        {
            element: 'Alternative Vehicle Options Accordion',
            action: 'Click',
            intention: 'View list of alternative vehicle options',
            location: ['Vehicle Search'],
            tagIndex: 'vs-025'
        },
        {
            ...(vehicle?.vin && { vehicle: mapVehicleToCDLData(vehicle) }),
            application: getApplication()
        }
    );
};
